import { Box, Typography } from "@outschool/backpack";
import { Activity, Section } from "@outschool/gql-backend-generated";
import {
  EmailPreviewQuery,
  EmailPreviewQueryVariables,
  SendActivityInviteEmailMutation,
  SendActivityInviteEmailMutationVariables,
} from "@outschool/gql-frontend-generated";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  gql,
  useMutation,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  ActivityListingPhoto,
  MeetingFragment,
} from "@outschool/ui-components-classroom";
import { ErrorMessage } from "@outschool/ui-components-shared";
import {
  LeaderFragment,
  useTopNoticeContext,
} from "@outschool/ui-components-website";
import {
  Form,
  Input,
  Loading,
  Modal,
  ModalActionButtons,
  TextArea,
} from "@outschool/ui-legacy-component-library";
import React, { SyntheticEvent, useCallback, useMemo, useState } from "react";

import { ActivityFragment } from "../../../shared/client/fragments/ActivityFragments";
import * as Env from "../../../shared/Env";
import useSectionString from "../../../shared/hooks/useSectionString";
import * as User from "../../../shared/User";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import {
  SectionEnrolledFragment,
  SectionFragment,
} from "../../queries/SectionFragments";

export const emailPreviewQuery = gql`
  query EmailPreview(
    $activityUid: ID
    $slugId: String
    $sectionUid: ID!
    $skipSection: Boolean!
  ) {
    activity(uid: $activityUid, slugId: $slugId) {
      ...ActivityFragment
      leader {
        ...LeaderFragment
      }
    }
    section(uid: $sectionUid) @skip(if: $skipSection) {
      filledSpaceCount
      ...SectionFragment
      ...SectionEnrolledFragment
      meetings {
        ...MeetingFragment
      }
    }
  }
  ${ActivityFragment}
  ${SectionFragment}
  ${SectionEnrolledFragment}
  ${MeetingFragment}
  ${LeaderFragment}
`;
interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  activityUid: string;
  sectionUid?: string;
  currentUserIsEnrolled: boolean;
}

export default ({
  isOpen,
  onClose,
  activityUid,
  sectionUid,
  currentUserIsEnrolled,
}: EmailModalProps) => {
  const { currentUserHasLoaded } = useSession();
  const { data, loading, error } = useQueryWithPreviousData<
    EmailPreviewQuery,
    EmailPreviewQueryVariables
  >(emailPreviewQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      activityUid,
      sectionUid: sectionUid || "",
      skipSection: !sectionUid,
    },
  });

  let content;
  if (loading || !currentUserHasLoaded) {
    content = <Loading data-test-id="loading" />;
  } else if (error) {
    content = (
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
    );
  } else if (data) {
    const { activity, section } = data;
    content = (
      <EmailParentsForm
        activity={activity}
        onClose={onClose}
        section={section}
        currentUserIsEnrolled={currentUserIsEnrolled}
      />
    );
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      {content}
    </Modal>
  );
};

interface EmailParentsFormProps {
  activity: EmailPreviewQuery["activity"];
  onClose: () => void;
  section: EmailPreviewQuery["section"];
  currentUserIsEnrolled: boolean;
  senderName?: string;
  recipientEmails?: string;
  campaignPrefix?: string;
}

export const EmailParentsForm = ({
  activity,
  onClose,
  section,
  currentUserIsEnrolled,
  recipientEmails,
  senderName,
  campaignPrefix,
}: EmailParentsFormProps) => {
  const { t } = useTranslation("client\\components\\sharing\\EmailModal");
  const [emailInvitation] = useMutation<
    SendActivityInviteEmailMutation,
    SendActivityInviteEmailMutationVariables
  >(gql`
    mutation SendActivityInviteEmail(
      $activityUid: ID!
      $recipientEmails: String!
      $isEnrolled: Boolean
      $sectionUid: ID
      $customMessage: String
      $campaignPrefix: String
    ) {
      sendActivityInviteEmail(
        activityUid: $activityUid
        recipientEmails: $recipientEmails
        isEnrolled: $isEnrolled
        sectionUid: $sectionUid
        customMessage: $customMessage
        campaignPrefix: $campaignPrefix
      )
    }
  `);
  const { currentUser: user, sessionToken } = useSession();
  const { timeZone } = useTimeZone();
  const { setTopNotice } = useTopNoticeContext();
  const trackEvent = useTrackEvent();
  const { emailSharingSubject, emailSharingMessage } = useSectionString();
  const senderNameValue = useMemo(() => {
    return senderName || User.senderName(user);
  }, [senderName, user]);
  const [recipientEmailsValue, setRecipientEmailsValue] = useState(
    recipientEmails || "",
  );
  const [sharingMessage, setSharingMessage] = useState(
    emailSharingMessage(senderNameValue, currentUserIsEnrolled),
  );
  const [error, setError] = useState(null);

  const handleEmailsChange = useCallback((e: $TSFixMe) => {
    setRecipientEmailsValue(e.target.value);
    setError(null);
  }, []);

  const handleMessageChange = useCallback((e: $TSFixMe) => {
    setSharingMessage(e.target.value);
    setError(null);
  }, []);

  const send = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!recipientEmailsValue) {
      setError(t("Please enter at least one email address") as any);
      return;
    }
    if (!sessionToken) {
      setError(t("Please log in to share") as any);
      return;
    }
    const variables = {
      activityUid: activity?.uid,
      sectionUid: section?.uid,
      isEnrolled: currentUserIsEnrolled,
      recipientEmails: recipientEmailsValue,
      customMessage: sharingMessage,
      campaignPrefix,
    };
    try {
      await emailInvitation({
        variables,
      });
      trackEvent("EMAIL_INVITATION_COMPLETED", {
        recipientSource: "manual",
        experimentAssignments: {},
      });
      onClose();
      setTopNotice(t("Email sent. Thank you for sharing!"));
    } catch (err) {
      setError(t("Error sending email") as any);
      OsPlatform.captureError(err, {
        extra: { variables },
        tags: { component: Component.Marketing },
      });
    }
  };

  const subject = emailSharingSubject(
    activity as Activity,
    section as Section,
    currentUserIsEnrolled,
    timeZone,
  );

  return (
    <Form name="email-parents">
      {!!error && (
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      )}
      <Typography
        variant="h3"
        sx={{
          marginBottom: "0.5em",
        }}
      >
        {t("Email a class invitation")}
      </Typography>
      <Typography variant="inherit" component="div">
        <b>{t("From:")} </b>
        {t("{{senderNameValue}} via Outschool", { senderNameValue })}
      </Typography>
      <Typography variant="inherit" component="div">
        <b>{t("Subject:")} </b>
        {subject}
      </Typography>
      <Box
        sx={{
          marginBottom: "0.5em",
        }}
      >
        <label htmlFor="to-emails">
          <b>{t("To:")} </b>
        </label>
        <Input
          type="text"
          id="to-emails"
          data-test-id="emails-field"
          sx={{ width: "100%" }}
          // eslint-disable-next-line i18next/no-literal-string
          placeholder="parent1@example.com, parent2@example.com"
          onChange={handleEmailsChange}
          value={recipientEmailsValue}
        />
      </Box>
      <Box
        sx={{
          marginBottom: "0.5em",
        }}
      >
        <label htmlFor="custom-message">
          <b>{t("Message:")} </b>
        </label>
        <TextArea
          id="custom-message"
          data-test-id="message-field"
          sx={{ width: "100%" }}
          placeholder={t("Add a personal note")}
          onChange={handleMessageChange}
          value={sharingMessage}
          showCount={false}
        />
      </Box>
      <ActivitySummary
        activity={activity}
        section={section}
        userTimeZone={timeZone}
      />
      <ModalActionButtons
        cancelLabel={t("Cancel")}
        onCancel={onClose}
        primaryLabel={t("Send")}
        onPrimary={send}
      />
    </Form>
  );
};

function ActivitySummary({ activity, section, userTimeZone }: $TSFixMe) {
  const { fullDurationString } = useSectionString();
  const { t } = useTranslation("client\\components\\sharing\\EmailModal");
  const { translate } = useMachineTranslation();
  if (!activity?.leader) {
    return null;
  }
  const { leader } = activity;
  return (
    <Box
      sx={{
        marginBottom: "1em",
        paddingBottom: "1em",
        borderBottom: "1px solid #E5E5E5",
      }}
    >
      <Box
        sx={{
          marginY: "1em",
        }}
      >
        <ActivityListingPhoto activity={activity} />
      </Box>
      <Typography variant="h5">{activity.titleTranslated}</Typography>
      <Typography variant="inherit">
        {t("by {{leaderName}}", { leaderName: leader.name })}
        {/* eslint-disable-next-line i18next/no-literal-string */}
        {leader.details.headline && ", " + translate(leader.details.headline)}
      </Typography>
      {section && (
        <Typography variant="inherit" component="div">
          {fullDurationString({
            activity,
            section,
            nextOngoingMeeting: section.nextOngoingMeeting,
            userTimeZone,
          })}
        </Typography>
      )}
    </Box>
  );
}
